// Only declare ProductCardGallery if it doesn't already exist
if (typeof ProductCardGallery === 'undefined') {
  class ProductCardGallery {
    constructor(element) {
      this.gallery = element;
      this.container = element.querySelector('[data-gallery-container]');
      this.slides = element.querySelectorAll('.product-card__gallery-slide');

      this.currentIndex = 0;
      this.totalSlides = this.slides.length;

      if (this.totalSlides <= 1) return;

      // Store gallery ID for event delegation
      this.galleryId = element.dataset.galleryId || Math.random().toString(36).substr(2, 9);
      element.dataset.galleryId = this.galleryId;
      element.dataset.currentIndex = '0';
    }

    init() {
      // No individual event listeners - using delegation instead
    }

    // Preload second image for hover effect on desktop
    static preloadSecondImage(gallery) {
      const slides = gallery.querySelectorAll('.product-card__gallery-slide');
      if (slides.length >= 2) {
        const secondSlide = slides[1];
        if (secondSlide && secondSlide.dataset.lazySlide === 'true') {
          ProductCardGallery.lazyLoadImage(secondSlide);
        }
      }
    }

    // Static methods for event delegation
    static lazyLoadImage(slide) {
      const imageSrc = slide.dataset.imageSrc;
      if (!imageSrc || slide.dataset.loaded === 'true') return;

      const placeholder = slide.querySelector('.lazy-image-placeholder');
      const spinner = slide.querySelector('.loading-spinner');

      if (placeholder && spinner) {
        // Show loading spinner with fade in
        spinner.style.display = 'block';
        spinner.style.opacity = '1';

        // Create and load the image
        const img = document.createElement('img');
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'cover';
        img.alt = 'Product image';

        img.onload = () => {
          // Fade out spinner first
          spinner.classList.add('fade-out');

          setTimeout(() => {
            // Replace placeholder with loaded image
            slide.innerHTML = '';
            slide.appendChild(img);

            // Trigger fade in animation
            requestAnimationFrame(() => {
              img.classList.add('loaded');
            });

            slide.dataset.loaded = 'true';
          }, 200); // Wait for spinner fade out
        };

        img.onerror = () => {
          // Handle error with fade effect
          spinner.classList.add('fade-out');
          setTimeout(() => {
            placeholder.innerHTML = '<span style="color: rgba(0,0,0,0.4); font-size: 12px;">Failed to load</span>';
          }, 200);
        };

        img.src = imageSrc;
      }
    }

    static goToSlide(gallery, index) {
      const container = gallery.querySelector('[data-gallery-container]');
      const slides = gallery.querySelectorAll('.product-card__gallery-slide');
      const totalSlides = slides.length;

      // Update index in dataset
      gallery.dataset.currentIndex = index;

      const translateX = -index * 100;
      container.style.transform = `translateX(${translateX}%)`;

      // Lazy load the current slide and next slide if they haven't been loaded
      const currentSlide = slides[index];
      const nextSlide = slides[index + 1];

      if (currentSlide && currentSlide.dataset.lazySlide === 'true') {
        ProductCardGallery.lazyLoadImage(currentSlide);
      }

      // Pre-load next slide for smoother experience
      if (nextSlide && nextSlide.dataset.lazySlide === 'true') {
        ProductCardGallery.lazyLoadImage(nextSlide);
      }
    }

    static next(gallery) {
      const currentIndex = parseInt(gallery.dataset.currentIndex || 0);
      const totalSlides = gallery.querySelectorAll('.product-card__gallery-slide').length;
      const nextIndex = (currentIndex + 1) % totalSlides;
      ProductCardGallery.goToSlide(gallery, nextIndex);
    }

    static prev(gallery) {
      const currentIndex = parseInt(gallery.dataset.currentIndex || 0);
      const totalSlides = gallery.querySelectorAll('.product-card__gallery-slide').length;
      const prevIndex = currentIndex === 0 ? totalSlides - 1 : currentIndex - 1;
      ProductCardGallery.goToSlide(gallery, prevIndex);
    }
  }
  // Make ProductCardGallery globally available
  window.ProductCardGallery = ProductCardGallery;
} // End of if (typeof ProductCardGallery === 'undefined')

// Event delegation setup - only once
if (typeof window.productCardDelegation === 'undefined') {
  window.productCardDelegation = true;

  // Single delegated event handler for all gallery interactions
  document.addEventListener('click', function(e) {
    // Handle gallery navigation buttons
    if (e.target.closest('[data-gallery-prev]')) {
      e.preventDefault();
      e.stopPropagation();
      const gallery = e.target.closest('.product-card__gallery');
      if (gallery) ProductCardGallery.prev(gallery);
    } else if (e.target.closest('[data-gallery-next]')) {
      e.preventDefault();
      e.stopPropagation();
      const gallery = e.target.closest('.product-card__gallery');
      if (gallery) ProductCardGallery.next(gallery);
    }
  });

  // Touch/swipe support with delegation
  let touchStartX = 0;
  let touchStartY = 0;
  let touchGallery = null;

  document.addEventListener('touchstart', function(e) {
    const container = e.target.closest('[data-gallery-container]');
    if (container) {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchGallery = container.closest('.product-card__gallery');
    }
  }, { passive: true });

  document.addEventListener('touchend', function(e) {
    if (touchGallery && e.changedTouches.length > 0) {
      const diffX = touchStartX - e.changedTouches[0].clientX;
      const diffY = Math.abs(touchStartY - e.changedTouches[0].clientY);

      if (Math.abs(diffX) > 30 && Math.abs(diffX) > diffY) {
        e.preventDefault();
        if (diffX > 0) {
          ProductCardGallery.next(touchGallery);
        } else {
          ProductCardGallery.prev(touchGallery);
        }
      }
      touchGallery = null;
    }
  }, { passive: false });

  // Intersection Observer for lazy initialization
  const galleryObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const productCard = entry.target;
        const gallery = productCard.querySelector('.product-card__gallery[data-image-count]');

        if (gallery && !gallery.dataset.initialized) {
          const imageCount = parseInt(gallery.dataset.imageCount);
          if (imageCount > 1) {
            new ProductCardGallery(gallery);
            gallery.dataset.initialized = 'true';
          }
          galleryObserver.unobserve(productCard);
        }
      }
    });
  }, { rootMargin: '100px' });

  // Start observing product cards when DOM is ready
  const observeCards = () => {
    document.querySelectorAll('product-card').forEach(card => {
      galleryObserver.observe(card);
    });
  };

  // Observe cards on DOM ready and after dynamic content loads
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCards);
  } else {
    observeCards();
  }

  // Re-observe for dynamically loaded content
  document.addEventListener('shopify:section:load', observeCards);

  // Desktop hover: preload second image on mouseenter for smooth hover effect
  // Only for devices with hover capability (desktop)
  const isDesktopWithHover = window.matchMedia('(hover: hover) and (min-width: 750px)').matches;

  if (isDesktopWithHover) {
    document.addEventListener('mouseenter', function(e) {
      const productMedia = e.target.closest('.product-card__media--hoverable');
      if (productMedia) {
        const gallery = productMedia.querySelector('.product-card__gallery');
        if (gallery && !gallery.dataset.hoverPreloaded) {
          // Preload the second image if lazy loaded
          ProductCardGallery.preloadSecondImage(gallery);
          gallery.dataset.hoverPreloaded = 'true';
        }
      }
    }, true); // Use capture phase for earlier detection
  }
}